/**
 * useHospitalExcel - Hook for Excel import/export operations
 *
 * Manages:
 * - Hospital import/export
 * - Locais import/export
 * - Representantes export
 * - Template downloads
 */
import { useState } from 'react';
import apiClient from '../services/apiClient';
import { msg, MESSAGES } from '../utils/messages';
import estabelecimentoService from '../services/estabelecimentoService';
import type { Hospital } from './useHospitalData';

// NOTE: apiClient already has baseURL with /api, so we use relative paths

interface ImportResult {
  success: number;
  created: number;
  updated: number;
  errors: string[];
}

interface ImportLocaisResult {
  success: boolean;
  created: number;
  updated: number;
  errors: string[];
}

interface BulkImportLocaisResult {
  success: boolean;
  hospitais_processados: number;
  locais_criados: number;
  locais_atualizados: number;
  total_locais: number;
  errors: string[];
  cnpjs_nao_encontrados: string[];
}

interface UseHospitalExcelProps {
  selectedHospital: Hospital | null;
  token: string | null;
  refreshHospitais: () => Promise<void>;
  refreshLocais: () => Promise<void>;
}

export function useHospitalExcel({
  selectedHospital,
  token,
  refreshHospitais,
  refreshLocais,
}: UseHospitalExcelProps) {
  // Import states
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLocaisFile, setImportLocaisFile] = useState<File | null>(null);
  const [bulkImportLocaisFile, setBulkImportLocaisFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importLocaisResult, setImportLocaisResult] = useState<ImportLocaisResult | null>(null);
  const [bulkImportLocaisResult, setBulkImportLocaisResult] = useState<BulkImportLocaisResult | null>(null);

  // Reset import states
  const resetImportState = () => {
    setImportFile(null);
    setImportResult(null);
  };

  const resetImportLocaisState = () => {
    setImportLocaisFile(null);
    setImportLocaisResult(null);
  };

  const resetBulkImportLocaisState = () => {
    setBulkImportLocaisFile(null);
    setBulkImportLocaisResult(null);
  };

  // Download hospital template
  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get(
        `/hospitais/template-excel`,
        {
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_hospitais.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Erro ao baixar template:', err);
      msg.error(MESSAGES.HOSPITAL.TEMPLATE_ERROR);
    }
  };

  // Import hospitals via Excel
  const handleImportHospitais = async () => {
    if (!importFile) {
      msg.warning(MESSAGES.HOSPITAL.SELECT_FILE);
      return;
    }

    try {
      setImporting(true);
      setImportResult(null);

      const formData = new FormData();
      formData.append('file', importFile);

      const response = await apiClient.post(
        `/hospitais/import-excel`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const created = response.data.created || 0;
      const updated = response.data.updated || 0;
      const total = created + updated;

      setImportResult({
        success: total,
        created,
        updated,
        errors: response.data.errors || []
      });

      // Show toast notification (same as manual add)
      if (total > 0) {
        const parts: string[] = [];
        if (created > 0) parts.push(`${created} ${created === 1 ? 'criado' : 'criados'}`);
        if (updated > 0) parts.push(`${updated} ${updated === 1 ? 'atualizado' : 'atualizados'}`);
        msg.success(`${total} ${total === 1 ? 'hospital importado' : 'hospitais importados'}: ${parts.join(', ')}`);
      }

      // Refresh hospital list
      await refreshHospitais();

    } catch (err: any) {
      console.error('Erro ao importar:', err);
      setImportResult({
        success: 0,
        created: 0,
        updated: 0,
        errors: [err.response?.data?.detail || err.response?.data?.message || 'Erro ao importar hospitais']
      });
    } finally {
      setImporting(false);
    }
  };

  // Export hospitals to Excel
  const handleExportExcel = async () => {
    try {
      await estabelecimentoService.exportExcel(true);
      msg.success(MESSAGES.HOSPITAL.EXCEL_EXPORTED);
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      msg.error(MESSAGES.HOSPITAL.EXCEL_EXPORT_ERROR);
    }
  };

  // Download locais template
  const handleDownloadLocaisTemplate = async () => {
    if (!selectedHospital) return;
    try {
      const response = await apiClient.get(
        `/hospitais/${selectedHospital.id}/locais/template-excel`,
        {
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_locais_${selectedHospital.codigo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao baixar template:', error);
      msg.error(MESSAGES.HOSPITAL.LOCAIS_TEMPLATE_ERROR);
    }
  };

  // Import locais via Excel
  const handleImportLocais = async () => {
    if (!selectedHospital || !importLocaisFile) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', importLocaisFile);

    try {
      const response = await apiClient.post(
        `/hospitais/${selectedHospital.id}/locais/import-excel`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setImportLocaisResult(response.data);

      // Show toast notification (same as manual add)
      const created = response.data.created || 0;
      const updated = response.data.updated || 0;
      const total = created + updated;
      if (total > 0) {
        const parts: string[] = [];
        if (created > 0) parts.push(`${created} ${created === 1 ? 'criado' : 'criados'}`);
        if (updated > 0) parts.push(`${updated} ${updated === 1 ? 'atualizado' : 'atualizados'}`);
        msg.success(`${total} ${total === 1 ? 'local de contagem importado' : 'locais de contagem importados'}: ${parts.join(', ')}`);
      }

      // Refresh locais
      await refreshLocais();
    } catch (error) {
      console.error('Erro ao importar locais:', error);
      setImportLocaisResult({ success: false, created: 0, updated: 0, errors: ['Erro ao importar arquivo'] });
    } finally {
      setImporting(false);
    }
  };

  // Export locais to Excel
  const handleExportLocais = async () => {
    if (!selectedHospital) return;
    try {
      const response = await apiClient.get(
        `/hospitais/${selectedHospital.id}/locais/export-excel`,
        {
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `locais_contagem_${selectedHospital.codigo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      msg.success(MESSAGES.HOSPITAL.LOCAIS_EXPORTED);
    } catch (error) {
      console.error('Erro ao exportar locais:', error);
      msg.error(MESSAGES.HOSPITAL.LOCAIS_EXPORT_ERROR);
    }
  };

  // Download bulk locais template
  const handleDownloadBulkLocaisTemplate = async () => {
    try {
      const response = await apiClient.get(
        `/hospitais/locais-contagem-bulk/template-excel`,
        {
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_locais_contagem_bulk.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao baixar template:', error);
      msg.error('Erro ao baixar template de upload em lote');
    }
  };

  // Bulk import locais via Excel
  const handleBulkImportLocais = async () => {
    if (!bulkImportLocaisFile) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', bulkImportLocaisFile);

    try {
      const response = await apiClient.post(
        `/hospitais/locais-contagem-bulk/import-excel`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setBulkImportLocaisResult(response.data);

      // Show toast notification
      const created = response.data.locais_criados || 0;
      const updated = response.data.locais_atualizados || 0;
      const hospitais = response.data.hospitais_processados || 0;
      const total = created + updated;

      if (total > 0) {
        const parts: string[] = [];
        if (created > 0) parts.push(`${created} ${created === 1 ? 'criado' : 'criados'}`);
        if (updated > 0) parts.push(`${updated} ${updated === 1 ? 'atualizado' : 'atualizados'}`);
        msg.success(`${total} locais de contagem importados (${parts.join(', ')}) para ${hospitais} ${hospitais === 1 ? 'hospital' : 'hospitais'}`);
      }

      // Refresh locais if a hospital is selected
      if (selectedHospital) {
        await refreshLocais();
      }
    } catch (error) {
      console.error('Erro ao importar locais em lote:', error);
      setBulkImportLocaisResult({
        success: false,
        hospitais_processados: 0,
        locais_criados: 0,
        locais_atualizados: 0,
        total_locais: 0,
        errors: ['Erro ao importar arquivo'],
        cnpjs_nao_encontrados: []
      });
    } finally {
      setImporting(false);
    }
  };

  // Export representantes to Excel
  const handleExportRepresentantes = async () => {
    if (!selectedHospital) return;
    try {
      const response = await apiClient.get(
        `/hospitais/${selectedHospital.id}/representantes/export-excel`,
        {
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `representantes_${selectedHospital.codigo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Erro ao exportar:', err);
      msg.error(MESSAGES.HOSPITAL.REP_EXPORT_ERROR);
    }
  };

  return {
    // Hospital import
    importFile,
    setImportFile,
    importResult,
    setImportResult,
    handleDownloadTemplate,
    handleImportHospitais,
    handleExportExcel,
    resetImportState,

    // Locais import
    importLocaisFile,
    setImportLocaisFile,
    importLocaisResult,
    setImportLocaisResult,
    handleDownloadLocaisTemplate,
    handleImportLocais,
    handleExportLocais,
    resetImportLocaisState,

    // Bulk locais import
    bulkImportLocaisFile,
    setBulkImportLocaisFile,
    bulkImportLocaisResult,
    setBulkImportLocaisResult,
    handleDownloadBulkLocaisTemplate,
    handleBulkImportLocais,
    resetBulkImportLocaisState,

    // Representantes
    handleExportRepresentantes,

    // Shared state
    importing,
  };
}
